import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import checkStatus from './jsFunctions/checkStatus.js'

import URL2 from './config.js'
import { Box, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

import MainTemplate from './components/MainTemplate/MainTemplate';
import PaymentPageTemplate from './components/PaymentPageTemplate/PaymentPageTemplate';
import StepBackButton from './components/StepBackButton/StepBackButton';
import LoadingBackdrop from './components/LoadingBackdrop/LoadingBackdrop.js';
import { storeCampusSessions } from './utils/campusPaymentCharges.js';
import { shouldRedirectToCompletion } from './utils/campusPaymentStatus.js';
import { countFeeEligibleChildren } from './utils/parentHistoryHelpers.js';
import {
  buildDefaultCampusPaymentSelections,
  isMultiCampusPayment,
  usesPerCampusPaymentOptions,
} from './utils/campusPaymentOptions.js';

/** A recalculation is usually quick, so wait this long before saying anything — otherwise the
 * indicator appears and disappears as a flicker. */
const RECALC_INDICATOR_DELAY_MS = 250;

/**
 * Payment page — fee math lives on backend (/calculatefees).
 */
const Payment = () => {
  const [feeCalculation, setFeeCalculation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // The first load has nothing on screen yet, so it blocks with the backdrop. Once the fees are
  // showing, an update keeps them visible and shows an inline indicator instead.
  const hasFeesRef = useRef(false);
  const recalcTimerRef = useRef(null);

  const startLoading = useCallback(() => {
    if (!hasFeesRef.current) {
      setLoading(true);
      return () => setLoading(false);
    }

    recalcTimerRef.current = setTimeout(() => setRecalculating(true), RECALC_INDICATOR_DELAY_MS);
    return () => {
      clearTimeout(recalcTimerRef.current);
      setRecalculating(false);
    };
  }, []);

  useEffect(() => () => clearTimeout(recalcTimerRef.current), []);

  let DiscountOption;
  let SM;
  let SD;
  let MC;
  let FTE;
  let ParentID;
  let college;
  let childrens;
  let enrolledchildrens;
  let SessionID;
  let campusSessions;
  let EmployeeId;
  let HCCExpiryDate;
  let GiveHCCDiscount;
  let SelectedHCCValue;
  let updatestud_detailsarr;
  let famName;
  let credit_amount;
  let isGhostLogin;
  let resumingCampusPayment;
  let campusPaymentSelections;
  let earlyBirdAvailable;

  if (localStorage.getItem("LoginToken") !== null) {
    DiscountOption = location.state.DiscountOption
    SM = location.state.SM
    SD = location.state.SD
    MC = location.state.MC
    FTE = location.state.FTE
    ParentID = location.state.ParentID
    college = location.state.college
    SessionID = location.state.SessionID
    campusSessions = location.state.campusSessions || []
    childrens = location.state.childrens
    enrolledchildrens = location.state.enrolledchildrens
    EmployeeId = location.state.EmployeeId
    credit_amount = location.state.credit_amount
    HCCExpiryDate = location.state.HCCExpiryDate
    GiveHCCDiscount = location.state.GiveHCCDiscount
    SelectedHCCValue = location.state.SelectedHCCValue
    updatestud_detailsarr = location.state.updatestud_detailsarr
    famName = location.state.famName
    isGhostLogin = location.state.isGhostLogin
    resumingCampusPayment = location.state.resumingCampusPayment
    campusPaymentSelections = location.state.campusPaymentSelections
    earlyBirdAvailable = location.state.earlyBirdAvailable !== false
  }

  // Fees depend on which children are selected (and their campus / HCC answers),
  // not just how many, so going back and changing a selection has to recalculate.
  const enrolledChildrenKey = (enrolledchildrens || [])
    .map((child) => [child?.stud_code, child?.campus_code || child?.cmpy_code, child?.HCC ? 'hcc' : ''].join(':'))
    .join('|');

  const isMultiCampus = isMultiCampusPayment([], campusSessions, enrolledchildrens);
  const [selectedCampusPayments, setSelectedCampusPayments] = useState(() => {
    if (Array.isArray(campusPaymentSelections) && campusPaymentSelections.length) {
      return campusPaymentSelections;
    }
    if (!usesPerCampusPaymentOptions({
      isMultiCampus,
      staffDiscount: SD,
      familyDiscountOption: DiscountOption,
    })) return undefined;
    return buildDefaultCampusPaymentSelections({
      campusSessions,
      familyDiscountOption: DiscountOption,
      giveHccDiscount: GiveHCCDiscount === true || GiveHCCDiscount === 'Yes',
      earlyBirdAvailable,
      staffDiscount: SD,
    });
  });

  const buildCalcPayload = useCallback((overrides = {}) => {
    const customAmount = typeof overrides === 'object' ? overrides.customAmount : overrides;
    const nextSelections = typeof overrides === 'object'
      ? overrides.campusPaymentSelections
      : undefined;
    return {
      SessionID,
      DiscountOption,
      SD,
      SM,
      MC,
      FTE,
      GiveHCCDiscount,
      NoOfChildren: countFeeEligibleChildren(enrolledchildrens),
      college,
      credit_amount,
      enrolledchildrens,
      persist: !isGhostLogin,
      campusSessions,
      earlyBirdAvailable,
      ...(customAmount !== undefined ? { customAmount } : {}),
      ...((nextSelections || selectedCampusPayments)
        ? { campusPaymentSelections: nextSelections || selectedCampusPayments }
        : {}),
    };
  }, [
    SessionID, DiscountOption, SD, SM, MC, FTE, GiveHCCDiscount,
    enrolledchildrens, college, credit_amount, isGhostLogin, campusSessions,
    earlyBirdAvailable, selectedCampusPayments,
  ]);

  const fetchFeeCalculation = useCallback(async (overrides) => {
    const stopLoading = startLoading();
    try {
      const result = await URL2.post('/calculatefees', buildCalcPayload(overrides || {}));
      setFeeCalculation(result.data);
      hasFeesRef.current = true;
      return result.data;
    } catch (error) {
      console.error('Error calculating school fees:', error);
      return null;
    } finally {
      stopLoading();
    }
  }, [buildCalcPayload, startLoading]);

  const updateRenrolmentdata = async () => {
    const stopLoading = startLoading();
    try {
      await URL2.post('/updaterenrolmentdata', {
        SessionID: SessionID,
        campusSessions: campusSessions,
        SM: SM,
        EmployeeId: EmployeeId,
        SelectedHCCValue: SelectedHCCValue,
        HCCExpiryDate: HCCExpiryDate,
        ref_page_complete: 'Yes',
        MC: MC,
        // Sent here as well as from the re-enrolment page, whose save is debounced: a parent who
        // answers the pay-in-full question and immediately presses Next can outrun it. The server
        // only writes this when it is sent, so arriving here without it changes nothing.
        StaffPayInFull: SD,
      });
    } catch (error) {
      console.error('Error updating enrolment data:', error);
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    const run = async () => {
      if (isGhostLogin) {
        await fetchFeeCalculation();
        return;
      }

      if (localStorage.getItem('LoginToken') === null) {
        navigate('/');
        return;
      }

      try {
        const data = await checkStatus({ campusSessions: campusSessions || [] });
        if (!data || data.RenrolStatus === undefined) return;

        // A submitted re-enrolment with a campus still owing keeps the parent on
        // this page so the remaining charges can be paid.
        if (shouldRedirectToCompletion(data)) {
          navigate('/reenrolmentcompleted', {
            state: { t_date: data.t_date, t_time: data.t_time, campusStatuses: data.campusStatuses || [] },
          });
          return;
        }

        if (Array.isArray(campusSessions) && campusSessions.length) {
          storeCampusSessions(campusSessions);
        }

        await fetchFeeCalculation();

        // A family resuming a part-paid re-enrolment reaches this page without the
        // discount form state, so the already-saved selections are left alone.
        if (!resumingCampusPayment) {
          await updateRenrolmentdata();
        }
      } catch (error) {
        console.error('An error occurred:', error);
      }
    };

    if (localStorage.getItem('LoginToken') !== null) {
      run();
    }
    
  }, [enrolledChildrenKey, GiveHCCDiscount, SM, DiscountOption, SD, FTE]);

  const handleCustomAmountRecalc = async (overrides) => {
    if (overrides && typeof overrides === 'object' && overrides.campusPaymentSelections) {
      setSelectedCampusPayments(overrides.campusPaymentSelections);
    }
    return fetchFeeCalculation(overrides);
  };

  if (localStorage.getItem('LoginToken') === null) {
    return null;
  }

  /**
   * Back to Re-enrolment, but only while nothing has been paid.
   *
   * Returning re-enters the fee flow. That is safe for a family who has paid nothing — the server
   * reuses the existing session rather than opening a second one (findExistingSession, which reuses
   * even a paid session "so a retry does not create a second Kewdale log and charge the family
   * twice") and re-quotes from what it already holds.
   *
   * It is not safe once a campus has been paid. A multi-campus family who has paid Kewdale and
   * returns to change a discount answer would be re-answering the questions that priced a payment
   * already taken, and nothing here can undo that payment. Those families carry on to settle what
   * is left; changing an answer after the fact is a conversation with AR, not a button.
   */
  const nothingPaidYet = !(feeCalculation?.campusBreakdown || [])
    .some((row) => row.paymentStatus === 'paid');

  const content = (
    <>
      {nothingPaidYet ? (
        <Box sx={{ mb: { xs: 1, sm: 1.5 } }}>
          <StepBackButton to="/reenrolment" label="Back" />
        </Box>
      ) : feeCalculation && (
        // Once a campus is paid the earlier screens can be looked at but not changed: changing an
        // answer would reprice a payment already taken. "Your answers" shows them, read-only.
        <Box sx={{ mb: { xs: 1, sm: 1.5 } }}>
          <StepBackButton to="/your-answers" label="Back — view your answers" />
        </Box>
      )}

      {recalculating && (
        <Box sx={{ position: 'sticky', top: 8, zIndex: 5, display: 'flex', justifyContent: 'center', mb: 1 }}>
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              bgcolor: '#ECFDF5', border: '1px solid #A7F3D0', color: '#065F46',
              px: 2, py: 1, borderRadius: '999px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            }}
          >
            <CircularProgress size={16} sx={{ color: '#059669' }} />
            <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>Updating your fees...</Typography>
          </Box>
        </Box>
      )}

      {/* Dimmed rather than hidden, so the parent can still see the figures being updated.
          Clicks are blocked to stop a second change landing mid-recalculation. */}
      <Box
        sx={{
          opacity: recalculating ? 0.55 : 1,
          pointerEvents: recalculating ? 'none' : 'auto',
          transition: 'opacity 150ms ease',
        }}
      >
        {feeCalculation && (
          <PaymentPageTemplate
            feeCalculation={feeCalculation}
            onRecalculate={handleCustomAmountRecalc}
            DiscountOption={DiscountOption}
            SM={SM}
            SD={SD}
            MC={MC}
            FTE={FTE}
            GiveHCCDiscount={GiveHCCDiscount}
            childrens={childrens}
            enrolledchildrens={enrolledchildrens}
            parentID={ParentID}
            college={college}
            famName={famName}
            SessionID={SessionID}
            campusSessions={campusSessions}
            updatestud_detailsarr={updatestud_detailsarr}
            credit_amount={credit_amount}
            isGhostLogin={isGhostLogin}
            campusPaymentSelections={selectedCampusPayments}
            earlyBirdAvailable={earlyBirdAvailable}
            campusStatuses={location.state?.campusStatuses || []}
          />
        )}
      </Box>
    </>
  );

  return (
    <Box>
      <LoadingBackdrop open={loading} icon={<CircularProgress sx={{ color: '#fff' }} />} />
      <MainTemplate college={`${college}`} content={content} SessionID={SessionID} />
    </Box>
  );
};

export default Payment;
